<script setup>
import { computed, onMounted, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore, elementLayoutTypeEnum } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import { useIsUpdated } from '@/composables/object'
import { useEscapeClose } from '@/composables/keyboard'
import { ROI_COMPARISON_COLORS, ROI_COMPARISON_NAME_MAX_LENGTH, resolveReferenceId, useRoiComparisonFormat, useRoiComparisonLimits, useRoiComparisonPersistence } from '@/composables/roi-comparison'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import CreateModeToggle from '@/components/shared/CreateModeToggle.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import RoiComparisonSelector from '@/components/roi/RoiComparisonSelector.vue'

const NAMED_PAIR_SIZE = 2

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: null,
  },
  initialStep: {
    type: Number,
    default: null,
  },
  initialQuickCreate: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update', 'cancel', 'delete'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { roiComparisonEditStepEnum: STEP } = planningStore
const { definitions, TYPE, typeLabel, groupLabel, itemName } = useRoiComparisonFormat()
const { saveAsync, fetchItemsAsync } = useRoiComparisonPersistence()

const isEdit = !!props.modelValue.id
const editItem = ref({
  type: props.modelValue.data?.type ?? TYPE.EQUIPMENT,
  items: props.items ?? [],
  referenceId: props.modelValue.data?.reference_id ?? null,
  name: props.modelValue.name ?? '',
  isShared: props.modelValue.is_shared ?? false,
})
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)
useEscapeClose(isUpdated, () => emit('cancel'))

const step = ref(props.initialStep ?? STEP.TYPE)
const quickCreate = ref(props.initialQuickCreate && !isEdit && !isMobile.value)
const pendingType = ref(null)
const showTypeConfirm = ref(false)
const showDeleteDialog = ref(false)
const takenNames = ref([])
const nextId = ref(null)

const { isComparable } = useRoiComparisonLimits(() => editItem.value.items)
const referenceId = computed(() => resolveReferenceId(editItem.value.items, editItem.value.referenceId))
const isNameTaken = (name) => takenNames.value.includes(name.trim())
const uniqueName = (name) => {
  let candidate = name
  for (let suffix = 2; isNameTaken(candidate); suffix++) candidate = `${name} (${suffix})`
  return candidate
}
const defaultName = computed(() => {
  const { items, type } = editItem.value
  if (items.length !== NAMED_PAIR_SIZE) return uniqueName(i18n.t('roiCompare.numberedName', { number: props.modelValue.id ?? nextId.value }))
  return uniqueName(items.map(item => itemName(type, item)).join(' — ').slice(0, ROI_COMPARISON_NAME_MAX_LENGTH - 10))
})
const referenceName = computed(() => {
  const reference = editItem.value.items.find(item => item.id === referenceId.value)
  return reference ? itemName(editItem.value.type, reference) : ''
})
const canSave = computed(() => isComparable.value && !!editItem.value.name.trim() && !isNameTaken(editItem.value.name))

const typeOptions = Object.values(TYPE).map(type => ({
  value: type,
  label: typeLabel(type),
  icon: definitions[type].icon,
  caption: definitions[type].groups.map(groupLabel).join(' · '),
}))

const isStepDone = (name) => isEdit || step.value > name

const setType = (type) => {
  editItem.value.type = type
  editItem.value.items = []
  editItem.value.referenceId = null
}

const onTypeSelect = (type) => {
  if (type === editItem.value.type) return
  if (!editItem.value.items.length) {
    setType(type)
    return
  }
  pendingType.value = type
  showTypeConfirm.value = true
}

const ensureDefaultName = () => {
  if (!editItem.value.name) editItem.value.name = defaultName.value
}

const goToStep = (name) => {
  if (name === STEP.REFERENCE) ensureDefaultName()
  step.value = name
}

const onSaveAsync = async() => {
  isUpdating.value = true
  const saved = await saveAsync({
    id: props.modelValue.id,
    name: editItem.value.name.trim() || defaultName.value,
    isShared: editItem.value.isShared,
    type: editItem.value.type,
    items: editItem.value.items,
    referenceId: referenceId.value,
  }).finally(() => { isUpdating.value = false })
  emit('update', saved, editItem.value.items)
}

const fetchTakenNamesAsync = async() => {
  const owner = props.modelValue.created_by ?? webSiteStore.user?.id
  const layouts = await webSiteStore.fetchElementLayoutsAsync(elementLayoutTypeEnum.ROI_COMPARISON)
  takenNames.value = layouts
    .filter(layout => layout.created_by === owner && layout.id !== props.modelValue.id)
    .map(layout => layout.name)
}

onMounted(async() => {
  const needsItems = !props.items && props.modelValue.data?.ids?.length
  const [, loadedItems, loadedNextId] = await Promise.all([
    fetchTakenNamesAsync(),
    needsItems ? fetchItemsAsync(editItem.value.type, props.modelValue.data.ids) : null,
    isEdit ? null : webSiteStore.fetchNextElementLayoutIdAsync(),
  ])
  nextId.value = loadedNextId
  if (loadedItems) editItem.value.items = loadedItems
  if (step.value === STEP.REFERENCE) ensureDefaultName()
  setOriginal(editItem.value)
})
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <navigate-back
      :text="isEdit ? i18n.t('roiCompare.editComparison') : i18n.t('roiCompare.addComparison')"
      @cancel="emit('cancel')"
    >
      <template v-if="!isEdit && !isMobile" #right>
        <create-mode-toggle v-model="quickCreate" />
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-px-sm" :class="{ 'dialog-card-content': isEdit }">
      <q-stepper v-if="quickCreate" :model-value="1" class="q-py-none">
        <q-step :name="1" :title="i18n.t('common.quickCreate')" icon="mdi-flash">
          <q-tabs
            :model-value="editItem.type"
            dense
            align="left"
            active-color="primary"
            indicator-color="primary"
            class="q-mb-sm"
            @update:model-value="onTypeSelect"
          >
            <q-tab
              v-for="option in typeOptions"
              :key="option.value"
              :name="option.value"
              :label="option.label"
            />
          </q-tabs>
          <roi-comparison-selector v-model="editItem.items" :type="editItem.type" />
          <div v-if="isComparable" class="row items-center no-wrap q-gutter-x-xs text-caption text-grey-7 q-mt-sm">
            <q-icon name="mdi-information-outline" size="xs" />
            <span>{{ i18n.t('roiCompare.quickCreateHint', { name: defaultName, reference: referenceName }) }}</span>
          </div>
          <stepper-navigation
            :finish="onSaveAsync"
            :finish-label="i18n.t('roiCompare.addComparison')"
            :disable="!isComparable"
            :loading="isUpdating"
          />
        </q-step>
      </q-stepper>
      <q-stepper v-else v-model="step" class="q-py-none">
        <q-step
          :name="STEP.TYPE"
          :title="i18n.t('roiCompare.step.type')"
          :caption="isStepDone(STEP.TYPE) ? typeLabel(editItem.type) : undefined"
          icon="mdi-format-list-bulleted-type"
          :done="isStepDone(STEP.TYPE)"
          :header-nav="isStepDone(STEP.TYPE)"
        >
          <q-select
            v-if="isMobile"
            :model-value="editItem.type"
            :label="i18n.t('common.type')"
            :options="typeOptions"
            emit-value
            map-options
            @update:model-value="onTypeSelect"
          >
            <template #prepend>
              <q-icon :name="definitions[editItem.type].icon" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section avatar>
                  <q-icon :name="scope.opt.icon" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.caption }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-list v-else>
            <q-item
              v-for="option in typeOptions"
              :key="option.value"
              v-ripple
              tag="label"
              :active="editItem.type === option.value"
              :active-class="`bg-${ROI_COMPARISON_COLORS.ACCENT}`"
            >
              <q-item-section side>
                <q-radio :model-value="editItem.type" :val="option.value" @update:model-value="onTypeSelect" />
              </q-item-section>
              <q-item-section avatar>
                <q-avatar :color="ROI_COMPARISON_COLORS.ACCENT" text-color="primary" :icon="option.icon" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ option.label }}</q-item-label>
                <q-item-label caption>{{ option.caption }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
          <stepper-navigation :continue="() => goToStep(STEP.ITEMS)" />
        </q-step>

        <q-step
          :name="STEP.ITEMS"
          :title="i18n.t('roiCompare.itemsToCompare')"
          :caption="isStepDone(STEP.ITEMS) ? `${editItem.items.length} ${i18n.t('roiCompare.item', editItem.items.length).toLowerCase()}` : undefined"
          :icon="definitions[editItem.type].icon"
          :done="isStepDone(STEP.ITEMS)"
          :header-nav="isStepDone(STEP.ITEMS)"
        >
          <roi-comparison-selector v-model="editItem.items" :type="editItem.type" />
          <stepper-navigation
            :back="() => goToStep(STEP.TYPE)"
            :continue="() => goToStep(STEP.REFERENCE)"
            :disable="!isComparable"
          />
        </q-step>

        <q-step
          :name="STEP.REFERENCE"
          :title="i18n.t('roiCompare.step.reference')"
          icon="mdi-target"
          :done="isEdit"
          :header-nav="isEdit || (isComparable && step > STEP.TYPE)"
        >
          <div class="row q-col-gutter-sm">
            <q-input
              v-model="editItem.name"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('roiCompare.comparisonName')"
              :maxlength="ROI_COMPARISON_NAME_MAX_LENGTH"
              :rules="[val => !!val?.trim() || i18n.t('common.required'), val => !isNameTaken(val) || i18n.t('roiCompare.nameExists')]"
            />
            <q-select
              :model-value="referenceId"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('roiCompare.referenceHint')"
              :options="editItem.items"
              option-value="id"
              :option-label="item => itemName(editItem.type, item)"
              emit-value
              map-options
              @update:model-value="editItem.referenceId = $event"
            >
              <template #prepend><q-icon name="mdi-target" /></template>
            </q-select>
            <q-toggle
              v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
              v-model="editItem.isShared"
              class="col-12"
              :label="i18n.t('common.shareWithEveryone')"
            />
          </div>
          <stepper-navigation
            :back="() => goToStep(STEP.ITEMS)"
            :finish="isEdit ? null : onSaveAsync"
            :finish-label="i18n.t('roiCompare.addComparison')"
            :disable="!canSave"
            :loading="isUpdating"
          />
        </q-step>
      </q-stepper>
    </q-card-section>

    <q-card-actions v-if="isEdit" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :disable="!canSave"
        :loading="isUpdating"
        @click="onSaveAsync"
      />
    </q-card-actions>

    <confirm-dialog
      v-model="showTypeConfirm"
      :message="i18n.t('roiCompare.changeTypeConfirm')"
      icon="mdi-swap-horizontal"
      @confirm="setType(pendingType)"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDeleteMessage', { item: modelValue.name })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="emit('delete', modelValue)"
    />
  </q-card>
</template>
